import {createContext, type RefObject, use} from 'react'

import type {BoardDropPlacement} from '../drag/placement'
import type {
  BoardCardAddress,
  BoardCell,
  BoardColumn,
  BoardData,
  BoardLabels,
  BoardRow,
  CellAddress,
  ItemId,
} from '../types'
import type {BoardCellItemIndex} from './cell-item-index'

/**
 * The board's state is split across several contexts rather than one, grouped by how
 * often each part changes. A card that only reads the board's labels must not re-render
 * because a cell paged, or because a different card was selected, or because a drag
 * started.
 *
 * **What the split protects is the board's own interaction state, and only that.** A
 * drag starting does re-render every drawn card, and no arrangement of these contexts
 * can stop it: `useDraggable` reads dnd-kit's own `InternalContext`, which is memoised on
 * the active draggable among other things, so every draggable on the board wakes when one
 * starts. What it costs is bounded. After the drag begins only the hovered droppable
 * changes, so a card re-renders per cell crossed rather than per pointer move, and a
 * draggable that is not the active one reads a null transform context and does not
 * re-render on movement at all. `@github-ui/board` windows its columns and rows, so the
 * cost is over the drawn cards rather than the board's. It is the same cost the two
 * dnd-kit boards already in this repository pay.
 *
 * The contexts that carry values:
 *
 * - `BoardDataContext`: the caller's `BoardData`, which changes on every fetch.
 * - `BoardConfigContext`: the accessors and strings the caller supplies once.
 * - `BoardCardDragPolicyContext`: the one question a card asks of the config. Separate
 *   from the config because a card is the only thing that asks it, and the config around
 *   it is rebuilt whenever a label, `getItemLabel`, `hasRowHeaders` or `resetScrollKey`
 *   changes identity. Every drawn card would then re-render for a label it never reads.
 * - `BoardPagingContext`: the three load-more callbacks. Separate from the config
 *   because they are the one part of it a caller is likely to rebuild per render, and a
 *   card reading a label must not re-render when they do.
 * - `BoardInteractionContext`: what the board is doing right now, which today is only
 *   which cards are being dragged. It is separate from the ones above because it changes
 *   on a gesture, and a card that reads a label must not re-render for that.
 * - `BoardCanMoveCardsContext`: whether this board moves cards at all. Separate from the
 *   one above because it is what the board *can* do rather than what it is doing, and it
 *   holds still for the life of the board while that one changes on every gesture.
 * - `BoardCellOrderingContext`: whether a cell's cards are in an order somebody chose.
 *   Beside the one above rather than in the config, on the same rule: it is another thing
 *   the board *can* do, only a cell asks it, and the config around it is rebuilt whenever a
 *   label or an accessor changes identity.
 * - `BoardDropPlacementContext`: a ref holding where in a cell the held card would land.
 *   It carries one ref object for the life of the board, so nothing re-renders for it.
 * - `BoardDragPointerYContext`: a ref holding how far down the viewport the pointer is
 *   during a drag, for the cell it is over to measure against. One ref object for the life
 *   of the board too.
 * - `BoardElementRefContext`: a ref to the board's own element, so that the paging sentinels
 *   can tell a movement of this board from anything else the page is doing. It holds one
 *   ref object for the life of the board, so nothing re-renders for it.
 * - `BoardDialogReportContext`, in `reporting/contexts.ts`: the one call a card makes to
 *   say it has opened or closed a dialog. It holds a callback and never changes, so a
 *   card reading it never re-renders for it.
 *
 * Selection has its own too, in `selection/contexts.ts`, on the same rule: it changes on
 * a gesture, and a card reading a label has no business re-rendering because a different
 * card was selected.
 */

/**
 * The data the caller passed, plus the indexes used to answer "which column, row or
 * cell sits at this key". All of them change together, on every new `data`, so they
 * share one context.
 */
export type BoardDataContextValue = {
  data: BoardData<unknown>
  columnsByKey: ReadonlyMap<string, BoardColumn>
  rowsByKey: ReadonlyMap<string, BoardRow>
  /** Keyed by `composeCellKey`. Holds only the populated pairs the caller supplied. */
  cellsByAddress: ReadonlyMap<string, BoardCell<unknown>>
  /**
   * Which item in a cell carries which id, built lazily and per cell.
   *
   * Here rather than beside either of the two things that read it, because both of them
   * would otherwise build one: a selected card is resolved to the caller's item, and so
   * is a card asking whether the caller lets it be dragged. One index per drawn card
   * would be a scan of the cell per card.
   */
  itemIndex: BoardCellItemIndex
}

export const BoardDataContext = createContext<BoardDataContextValue | undefined>(undefined)

export type BoardConfig = {
  getItemId: (item: unknown) => ItemId
  getItemLabel: (item: unknown) => string
  labels: BoardLabels
  /**
   * Whether the caller gives its rows a visible name. A board with no swimlane has one
   * row that means nothing to the reader, so naming its cells after that row would put
   * a label nobody can see into every cell's accessible name.
   *
   * The caller states this. Deriving it from `rows.length` would be the single-row
   * special case the contract forbids, and it would flicker on a swimlaned board whose
   * second row has not paged in yet.
   */
  hasRowHeaders: boolean
  /**
   * Changing this puts both scroll containers back to the start. The board never
   * restores a scroll position, because only the caller knows whether new data is the
   * same board scrolled further or a different one. Undefined means never reset.
   */
  resetScrollKey?: string | number
  /**
   * How fast the board scrolls itself under a dragged card, in pixels per animation frame
   * at full velocity. Covers going across the board and going down it to a swimlane that
   * is off screen, which are one journey rather than two.
   */
  autoScrollRate: number
  /**
   * The same, down the cards inside one cell. Its own number because a card is a smaller
   * target than a column. See `scroll/settings.ts` for what the two mean for a drag that
   * crosses between them.
   */
  cellAutoScrollRate: number
}

export const BoardConfigContext = createContext<BoardConfig | undefined>(undefined)

/**
 * Why the caller refuses to let one card be dragged, or absent because it does not.
 *
 * Takes an address rather than an item, because a card knows where it is and the caller's
 * predicate takes the caller's own item. Resolving between the two is the provider's, so
 * a card never touches the data to ask.
 *
 * Absent covers three things that all leave a card draggable: the caller allowed it, the
 * caller's data no longer holds it, and it has no id to look one up by. A card that cannot
 * be dragged for one of the last two has no reason anybody stated, and this package has
 * none of its own to invent.
 *
 * **Its own context so that a card subscribes to this and to nothing else.** A card
 * reading the config would wake for every label and accessor in it, and `useBoardCard` is
 * the one hook that runs per drawn card. What is left is the narrowest set that can change
 * the answer: the caller's data, `getItemId`, and `getItemDragState`. The first two already
 * re-render every card through `BoardFocusGridContext`, so only the third is new.
 */
export type BoardCardDragPolicy = (cellKey: string, itemId: ItemId) => string | undefined

export const BoardCardDragPolicyContext = createContext<BoardCardDragPolicy | undefined>(undefined)

/**
 * The three questions a board can ask, and the three different words it asks them in.
 *
 * They are three because they mean three different things, and a caller handed one
 * callback for all of them would have to guess which axis moved. Reaching the end of a
 * cell's cards says nothing about the columns; reaching the end of the columns says
 * nothing about the rows.
 *
 * Every one of them is optional. A board whose columns are all there simply supplies no
 * `onLoadMoreColumns`, and nothing observes that direction.
 *
 * The board fetches nothing itself, so none of these returns anything. Each reports that
 * a direction was reached, and the next page arrives as new `data`.
 */
export type BoardPaging = {
  /** More cards in one cell. Always given both halves of the address. */
  onLoadMoreItems?: (address: CellAddress) => void
  onLoadMoreColumns?: () => void
  onLoadMoreRows?: () => void
}

export const NO_PAGING: BoardPaging = {}

export const BoardPagingContext = createContext<BoardPaging>(NO_PAGING)

export function useBoardPaging(): BoardPaging {
  return use(BoardPagingContext)
}

/**
 * What the board is doing at this moment, as opposed to what it holds.
 *
 * Only the dragged cards, which `drag/BoardDragProvider.tsx` publishes. The auto-scroll
 * in `useBoardRoot` and `useBoardCell` reads from here, and so does the report that tells
 * a caller a drag has begun, so all three work off one fact. That is the point of the
 * seam: behaviour arrives inside a hook, not through a new prop.
 *
 * Cards rather than ids, because a drag moves a card between cells and which cell it came
 * from is the whole of what a drag is about. See {@link BoardCardAddress}.
 *
 * **Whatever publishes this has to be an ancestor of the reporting provider.** Three
 * things read it: the auto-scroll in `useBoardRoot` and `useBoardCell`, which a caller
 * renders inside `children`, and the drag report in `reporting/`, which `BoardProvider`
 * renders itself and which is therefore above `children`. A publisher placed in
 * `children` would reach the first two and be invisible to the third, so the drag would
 * scroll the board and never be reported.
 */
export type BoardInteraction = {
  draggingCards: readonly BoardCardAddress[]
}

export const NO_DRAGGING_CARDS: readonly BoardCardAddress[] = []

export const BoardInteractionContext = createContext<BoardInteraction>({draggingCards: NO_DRAGGING_CARDS})

export function useBoardInteraction(): BoardInteraction {
  return use(BoardInteractionContext)
}

/**
 * Whether a card this board draws has anywhere to be dropped, which is true exactly when
 * the caller supplied `onItemsMove`. Published by `drag/BoardDragProvider.tsx`.
 *
 * **It is the whole of what makes the board a drag surface.** A card registers as a drag
 * source and a cell registers as a drop target only while this is true, so a caller that
 * says nothing about moves gets a board no card can be lifted off and no cell can be
 * dropped on. Gating anything narrower leaves the gesture answering every way a gesture
 * can -- the card lifts, a copy follows the pointer, the cells scroll under it -- before
 * discarding what it was told, which is an offer the board cannot honour.
 *
 * A different question from `getItemDragState`, which is why it is not folded into
 * {@link BoardCardDragPolicy}: that one says which cards may move on a board that moves
 * cards, and carries the caller's reason for each it refuses. This says whether the board
 * moves any, and no caller stated it about any particular card.
 *
 * Its own context, and a bare boolean, because it changes only if a caller adds or removes
 * the handler entirely. Both `useBoardCard` and `useBoardCell` read it, and React compares
 * it with `Object.is`, so subscribing costs a board at rest nothing.
 *
 * `false` by default, which is unreachable through `BoardProvider`: it renders the drag
 * provider itself, so every card and cell is below one.
 */
export const BoardCanMoveCardsContext = createContext(false)

export function useBoardCanMoveCards(): boolean {
  return use(BoardCanMoveCardsContext)
}

/**
 * Whether the cards in one cell are in an order somebody chose, rather than in the order
 * the board's sort puts them.
 *
 * Only the caller knows. A board ordered by a sort has no position inside a cell for a
 * reader to aim at, so dropping a card among its own neighbours changes nothing there; a
 * board ordered by hand -- a sub-issue list, a manually ranked backlog -- is one where that
 * gesture is the whole point. The board holds no rule about which it is and derives none.
 *
 * Asked per cell rather than per board, because the same board can hold both: a column
 * standing for a value nobody ranks sits beside one that is ranked.
 *
 * `false` by default, which is every board that existed before this did: the cells stay the
 * only drop targets, a drop reports which cell and nothing more, and no cell offers a place
 * to aim at.
 */
export type BoardCellOrdering = (cell: CellAddress) => boolean

function noCellIsHandOrdered(): boolean {
  return false
}

export const BoardCellOrderingContext = createContext<BoardCellOrdering>(noCellIsHandOrdered)

export function useBoardCellOrdering(): BoardCellOrdering {
  return use(BoardCellOrderingContext)
}

/**
 * Where in a cell the drag being held would land, written by the cell the pointer is over
 * and read once, by `drag/BoardDragProvider.tsx`, when the drop happens.
 *
 * A ref rather than state, because it changes as fast as the pointer does and nothing above
 * the cell draws it. The cell that owns it keeps its own copy in state, which is what draws
 * the place a card would go; this is the same fact travelling to the one handler that has
 * to report it, and putting it in state here would re-render the board on every pointer
 * move.
 *
 * Exactly one cell writes at a time, because exactly one cell is under the pointer, and it
 * clears the ref when the drag leaves it. `null` therefore means what it says: the cell
 * under the pointer has no position to report, either because its caller does not
 * hand-order it or because the reader is aiming where the card already is.
 */
const NO_DROP_PLACEMENT: RefObject<BoardDropPlacement | null> = {current: null}

export const BoardDropPlacementContext = createContext<RefObject<BoardDropPlacement | null>>(NO_DROP_PLACEMENT)

export function useBoardDropPlacementRef(): RefObject<BoardDropPlacement | null> {
  return use(BoardDropPlacementContext)
}

/**
 * How far down the viewport the pointer is while this board carries one of its own cards,
 * in client coordinates. `null` between drags, and until the first move of one.
 *
 * Written by `drag/BoardDragProvider.tsx` and read by the cell under the pointer, which
 * measures a place inside itself against it. That cell hears every move made while it is
 * the one under the pointer; what it cannot hear is the move that carried the drag into
 * it, which happened before it had anything bound. Without this, a reader who crosses into
 * a cell and stops -- to let the cell scroll to what they are aiming at, or to wait for a
 * page -- is answered with no place at all, because nothing ever said where they were.
 *
 * Down the viewport and no more, because a place inside a cell is a place in a column of
 * cards: `drag/placement.ts` measures one coordinate and there is no second question to
 * ask of the pointer here.
 *
 * A ref for the reason the placement above is one: it changes as fast as the pointer does
 * and nothing draws it directly, so it carries one ref object for the life of the board and
 * nothing re-renders for it.
 */
const NO_DRAG_POINTER: RefObject<number | null> = {current: null}

export const BoardDragPointerYContext = createContext<RefObject<number | null>>(NO_DRAG_POINTER)

export function useBoardDragPointerYRef(): RefObject<number | null> {
  return use(BoardDragPointerYContext)
}

/**
 * What a board reads before its root has mounted, and what one assembled without
 * `useBoardRoot` reads for as long as it lives. Shared, because nothing writes to it: the
 * only writer is `useBoardRoot`, which always runs under a provider and is therefore
 * always given the provider's own.
 */
const NO_BOARD_ELEMENT: RefObject<HTMLElement | null> = {current: null}

/**
 * The board's own element, which is the box a movement has to happen inside to be about
 * this board.
 *
 * Published so that the paging sentinels can tell the reader scrolling *this* board from
 * anything else the page is doing. They listen for input on the window, because a reader
 * who has run out of cell to scroll moves nothing and the events are the only sign of
 * them at all, and a listener with nothing to compare against hears every wheel, key and
 * click on the page.
 *
 * A ref rather than state, because the only thing that reads it does so inside an event
 * handler. Held here rather than in `useBoardRoot`, because the element belongs to the
 * caller's tree and the things that ask about it are drawn all over it.
 *
 * Null until the root mounts, and null for good on a board assembled without one, which
 * reads as "no box to be inside" rather than as an empty one: such a board hears every
 * movement, exactly as it did before there was anything to scope to.
 */
export const BoardElementRefContext = createContext<RefObject<HTMLElement | null>>(NO_BOARD_ELEMENT)

export function useBoardElementRef(): RefObject<HTMLElement | null> {
  return use(BoardElementRefContext)
}

export function useBoardDataContext(): BoardDataContextValue {
  const value = use(BoardDataContext)
  if (!value) {
    throw new Error('This hook must be called inside a BoardProvider.')
  }

  return value
}

export function useBoardConfig(): BoardConfig {
  const value = use(BoardConfigContext)
  if (!value) {
    throw new Error('This hook must be called inside a BoardProvider.')
  }

  return value
}

export function useBoardCardDragPolicy(): BoardCardDragPolicy {
  const value = use(BoardCardDragPolicyContext)
  if (!value) {
    throw new Error('This hook must be called inside a BoardProvider.')
  }

  return value
}
